import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { GraduationCap, Search, Eye, Trash2, Download, CheckCircle, XCircle, Clock, FileText, X, ArrowRight, Edit, MessageSquare, Upload } from 'lucide-react';
import { resolveFileUrl } from '../../utils/fileUrl';
import CommentThread from '../../components/CommentThread';
import { useAuth } from '../../context/AuthContext';
import { formatDisplayDate, formatDisplayDateTime } from '../../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ScholarshipApplications = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [appAdminComments, setAppAdminComments] = useState('');
  const [savingComments, setSavingComments] = useState(false);
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [appInternalComments, setAppInternalComments] = useState('');
  const [savingInternal, setSavingInternal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const enterEditMode = () => {
    if (!selectedApp) return;
    setEditForm({ ...selectedApp });
    setEditMode(true);
  };

  const cancelEditMode = () => {
    setEditMode(false);
    setEditForm({});
  };

  const handleEditField = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveEdit = async () => {
    if (!selectedApp) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      const editableKeys = [
        'fullName','fatherName','motherName','dateOfBirth','gender',
        'email','phone','whatsapp','address','city','state','pincode',
        'familyIncome','familyMembers','fatherOccupation',
        'currentCourse','courseDuration','currentYear','previousMarks',
        'admissionYear','instituteName','instituteAddress','instituteCity','instituteState',
        'instituteBankName','instituteBankAccount','instituteBankIfsc','tuitionFeeAmount',
        'otherScholarship','otherScholarshipDetails','remarks',
      ];
      const payload = editableKeys.reduce((acc, k) => {
        if (editForm[k] !== undefined) acc[k] = editForm[k];
        return acc;
      }, {});
      const { data } = await axios.put(
        `${API}/scholarship/applications/${selectedApp.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = data.application;
      setSelectedApp(updated);
      setApplications((list) => list.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
      setEditMode(false);
      setEditForm({});
      toast.success('Application updated');
      if (showHistory) fetchHistory(selectedApp.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update application');
    } finally {
      setSavingEdit(false);
    }
  };

  // Renders a labeled read-only value OR an editable input, depending on edit mode.
  // `render` is used when NOT editing (e.g. currency-formatted display).
  const renderField = (label, key, { type = 'text', render, colSpan, placeholder } = {}) => {
    const value = editMode ? (editForm[key] ?? '') : (selectedApp[key] ?? '');
    return (
      <div className={colSpan ? `col-span-${colSpan}` : ''}>
        <p className="text-sm text-muted-foreground">{label}</p>
        {editMode ? (
          <Input
            type={type}
            value={value ?? ''}
            onChange={(e) => handleEditField(key, e.target.value)}
            placeholder={placeholder || label}
            data-testid={`edit-scholarship-${key}`}
          />
        ) : (
          <p className="font-medium">{render ? render(selectedApp[key]) : (selectedApp[key] ?? '—')}</p>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // Reset per-record state whenever the modal opens on a new application
  useEffect(() => {
    if (selectedApp) {
      setAppAdminComments(selectedApp.adminComments || '');
      setAppInternalComments(selectedApp.internalComments || '');
      setShowHistory(false);
      setHistory([]);
      setEditMode(false);
      setEditForm({});
    }
  }, [selectedApp?.id]);

  const fetchHistory = async (appId) => {
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/scholarship/applications/${appId}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(data.history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load edit history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const saveAdminComments = async (text) => {
    if (!selectedApp) return;
    const token = localStorage.getItem('token');
    const { data } = await axios.put(
      `${API}/scholarship/applications/${selectedApp.id}/comments`,
      { comment: text },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const patch = { adminCommentsHistory: data.history || [], adminComments: data.adminComments || '' };
    setSelectedApp((prev) => ({ ...prev, ...patch }));
    setApplications((list) => list.map((a) => (a.id === selectedApp.id ? { ...a, ...patch } : a)));
    if (showHistory) await fetchHistory(selectedApp.id);
    toast.success('Comment added. Applicant notified by email.');
  };

  const saveInternalComments = async (text) => {
    if (!selectedApp) return;
    const token = localStorage.getItem('token');
    const { data } = await axios.put(
      `${API}/scholarship/applications/${selectedApp.id}/internal-comments`,
      { comment: text },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const patch = { internalCommentsHistory: data.history || [], internalComments: data.internalComments || '' };
    setSelectedApp((prev) => ({ ...prev, ...patch }));
    setApplications((list) => list.map((a) => (a.id === selectedApp.id ? { ...a, ...patch } : a)));
    if (showHistory) await fetchHistory(selectedApp.id);
    toast.success('Internal note added. Not shared with applicant.');
  };

  const downloadAsPdf = async () => {
    if (!selectedApp) return;
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API}/scholarship/applications/${selectedApp.id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const cd = resp.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = (match && match[1]) || `scholarship-${selectedApp.id.slice(0, 8)}.pdf`;
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF download failed');
    }
  };

  const uploadAdditionalDoc = async (file, label, note) => {
    if (!selectedApp || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    if (label) fd.append('label', label);
    if (note) fd.append('note', note);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/scholarship/applications/${selectedApp.id}/documents`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newDoc = data.document;
      setSelectedApp((prev) => ({ ...prev, additionalDocuments: [...(prev.additionalDocuments || []), newDoc] }));
      setApplications((list) => list.map((a) => (a.id === selectedApp.id ? { ...a, additionalDocuments: [...(a.additionalDocuments || []), newDoc] } : a)));
      if (showHistory) await fetchHistory(selectedApp.id);
      toast.success('Document uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const submitDisbursement = async (file, remark) => {
    if (!selectedApp) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('remark', remark);
    const token = localStorage.getItem('token');
    const { data } = await axios.post(
      `${API}/scholarship/applications/${selectedApp.id}/disburse`,
      fd,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const patch = { status: 'disbursed', disbursement: data.disbursement, disbursementRemark: data.disbursement?.remark, disbursedAt: data.disbursedAt };
    setSelectedApp((prev) => ({ ...prev, ...patch }));
    setApplications((list) => list.map((a) => (a.id === selectedApp.id ? { ...a, ...patch } : a)));
    if (showHistory) await fetchHistory(selectedApp.id);
    toast.success('Marked as Disbursed. The applicant has been notified.');
  };

  const deleteAdditionalDoc = async (docId) => {
    if (!selectedApp) return;
    if (!window.confirm('Remove this document from the application?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/scholarship/applications/${selectedApp.id}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const filterFn = (arr) => (arr || []).filter((d) => d.id !== docId);
      setSelectedApp((prev) => ({ ...prev, additionalDocuments: filterFn(prev.additionalDocuments) }));
      setApplications((list) => list.map((a) => (a.id === selectedApp.id ? { ...a, additionalDocuments: filterFn(a.additionalDocuments) } : a)));
      if (showHistory) await fetchHistory(selectedApp.id);
      toast.success('Document removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove document');
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scholarship/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications || []);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (appId, status, remarks = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/scholarship/applications/${appId}/status?status=${status}&remarks=${encodeURIComponent(remarks)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Application ${status} successfully`);
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/scholarship/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application deleted successfully');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Father Name', 'Email', 'Phone', 'Institute', 'Course', 'Year', 'Marks %', 'Fee Amount', 'Status', 'Application Date'];
    const csvData = applications.map(a => [
      a.fullName,
      a.fatherName,
      a.email,
      a.phone,
      a.instituteName,
      a.currentCourse,
      a.currentYear,
      a.previousMarks,
      `₹${a.tuitionFeeAmount}`,
      a.status,
      formatDisplayDate(a.applicationDate)
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'scholarship_applications.csv';
    a.click();
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.instituteName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      disbursed: 'bg-blue-100 text-blue-800',
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
      disbursed: <CheckCircle className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {icons[status]} {status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif font-bold text-3xl text-primary mb-2">Scholarship Applications</h1>
          <p className="text-muted-foreground">Student scholarship applications</p>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-green-600" />
            <span className="text-lg font-semibold">{applications.length} Applications</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or institute..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'approved', 'rejected', 'disbursed'].map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className="capitalize"
              >
                {status} ({status === 'all' ? applications.length : applications.filter(a => a.status === status).length})
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Applications Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Student</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Institute</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Course</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Marks</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Fee</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    Loading applications...
                  </td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    No applications found
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground">{app.fullName}</p>
                      <p className="text-sm text-muted-foreground">{app.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{app.instituteName}</p>
                      <p className="text-sm text-muted-foreground">{app.instituteCity}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{app.currentCourse}</p>
                      <p className="text-sm text-muted-foreground">Year {app.currentYear}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600">{app.previousMarks}%</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold">₹{parseInt(app.tuitionFeeAmount).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedApp(app)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {app.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => updateApplicationStatus(app.id, 'approved')}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => updateApplicationStatus(app.id, 'rejected')}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {!(app.status === 'approved' && !user?.superAdmin) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteApplication(app.id)}
                            data-testid={`delete-application-${app.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedApp(null)}>
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-serif font-bold text-2xl text-primary">{selectedApp.fullName}</h2>
                  <p className="text-muted-foreground">Scholarship Application</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedApp.status)}
                  {!editMode ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={enterEditMode}
                      data-testid="scholarship-edit-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        onClick={saveEdit}
                        disabled={savingEdit}
                        data-testid="scholarship-edit-save-btn"
                      >
                        {savingEdit ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={cancelEditMode}
                        disabled={savingEdit}
                        data-testid="scholarship-edit-cancel-btn"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadAsPdf}
                    data-testid="scholarship-download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => (showHistory ? setShowHistory(false) : fetchHistory(selectedApp.id))}
                    disabled={historyLoading}
                    data-testid="scholarship-history-btn"
                  >
                    {historyLoading ? <Clock className="w-4 h-4 animate-spin mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
                    History
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedApp(null)} data-testid="scholarship-modal-close">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Edit History Panel */}
              {showHistory && (
                <div className="mb-4 bg-purple-50/50 border border-purple-200 rounded-lg p-4" data-testid="scholarship-history-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-purple-600" />
                      <h4 className="font-semibold text-sm text-purple-900">Edit History / Activity Log</h4>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{history.length} entries</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setShowHistory(false)} className="text-purple-600 h-7 px-2">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {historyLoading ? (
                    <p className="text-sm text-gray-500 text-center py-3">Loading…</p>
                  ) : history.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-3">No edit history recorded yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {history.map((entry, idx) => (
                        <div key={entry.id || idx} className="bg-white rounded-lg border border-gray-100 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                entry.changeType === 'status_change' ? 'bg-blue-100 text-blue-700' :
                                entry.editedByRole === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {entry.changeType === 'status_change' ? 'Status Change' : entry.editedByRole === 'admin' ? 'Admin Edit' : 'User Edit'}
                              </span>
                              <span className="text-xs text-gray-500">by <span className="font-medium">{entry.editedBy}</span></span>
                            </div>
                            <span className="text-xs text-gray-400">
                              {formatDisplayDateTime(entry.timestamp)}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {entry.changes?.map((change, cidx) => (
                              <div key={cidx} className="flex items-center gap-2 text-xs py-1 border-t border-gray-50 first:border-t-0">
                                <span className="font-medium text-gray-700 min-w-[120px]">{change.label}:</span>
                                <span className="text-red-500 line-through truncate max-w-[150px]" title={change.oldValue}>{change.oldValue}</span>
                                <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className="text-green-600 font-medium truncate max-w-[150px]" title={change.newValue}>{change.newValue}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{entry.totalChanges} field(s) changed</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Status Timeline — automatic timestamps */}
              <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="scholarship-timeline">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <h4 className="text-sm font-semibold text-slate-800">Status Timeline</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {[
                    { key: 'applicationDate', label: 'Application Received', color: 'text-gray-700' },
                    { key: 'underReviewAt', label: 'Under Review', color: 'text-blue-700' },
                    { key: 'approvedAt', label: 'Approved', color: 'text-green-700' },
                    { key: 'rejectedAt', label: 'Rejected', color: 'text-red-700' },
                    { key: 'disbursedAt', label: 'Disbursed', color: 'text-indigo-700' },
                  ].map((item) => {
                    const raw = selectedApp[item.key];
                    if (!raw) return null;
                    const d = new Date(raw);
                    const isValid = !isNaN(d.getTime());
                    return (
                      <div key={item.key} className="flex items-baseline justify-between px-3 py-1.5 bg-white rounded border border-slate-100">
                        <span className={`font-medium ${item.color}`}>{item.label}</span>
                        <span className="text-xs text-slate-500 ml-3">
                          {isValid
                            ? formatDisplayDateTime(raw)
                            : String(raw)}
                        </span>
                      </div>
                    );
                  })}
                  {!(selectedApp.underReviewAt || selectedApp.approvedAt || selectedApp.rejectedAt || selectedApp.disbursedAt) && (
                    <p className="col-span-full text-xs text-slate-400 italic">No status transitions yet. Timestamps will appear here automatically as the application moves through Under Review / Approved / Rejected / Disbursed.</p>
                  )}
                </div>
              </div>

              {/* Disbursement details — visible when the record has been disbursed */}
              {selectedApp.disbursement && (
                <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4" data-testid="scholarship-disbursement-card">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-indigo-700" />
                    <h4 className="text-sm font-semibold text-indigo-900">Disbursement Details</h4>
                  </div>
                  <div className="text-sm text-indigo-900 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold min-w-[110px] text-indigo-700">Remark</span>
                      <span className="whitespace-pre-wrap flex-1">{selectedApp.disbursement.remark}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-semibold min-w-[110px] text-indigo-700">Proof</span>
                      <div className="flex-1">
                        <div className="flex gap-3 items-center">
                          <a
                            href={resolveFileUrl(selectedApp.disbursement.proofUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                            data-testid="view-disbursement-proof"
                          >
                            <FileText className="w-4 h-4" /> View
                          </a>
                          <a
                            href={`${resolveFileUrl(selectedApp.disbursement.proofUrl)}${resolveFileUrl(selectedApp.disbursement.proofUrl).includes('?') ? '&' : '?'}download=1`}
                            className="text-green-600 hover:underline text-sm flex items-center gap-1"
                            data-testid="download-disbursement-proof"
                          >
                            <Download className="w-4 h-4" /> Download
                          </a>
                          {selectedApp.disbursement.proofOriginalName && (
                            <span className="text-xs text-indigo-600 truncate">
                              {selectedApp.disbursement.proofOriginalName}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-indigo-600 pt-1 border-t border-indigo-100">
                      <span>Recorded by <strong>{selectedApp.disbursement.recordedBy}</strong></span>
                      {selectedApp.disbursement.recordedAt && (
                        <span>· {formatDisplayDateTime(selectedApp.disbursement.recordedAt)}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Internal Comments — committee only, never shown/emailed to applicant */}
              <CommentThread
                entries={selectedApp.internalCommentsHistory || []}
                legacyText={selectedApp.internalComments || ''}
                onSubmit={saveInternalComments}
                title="Internal Comments"
                subtitle="These notes are never emailed to the applicant."
                testIdPrefix="scholarship-internal-comments"
                variant="gray"
              />

              {/* Admin Comments / Feedback */}
              <CommentThread
                entries={selectedApp.adminCommentsHistory || []}
                legacyText={selectedApp.adminComments || ''}
                onSubmit={saveAdminComments}
                title="Committee Feedback / Comments"
                subtitle="Each new entry is emailed to the applicant."
                testIdPrefix="scholarship-comments"
                variant="amber"
              />

              {/* Header full name */}
              {editMode && (
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <Input
                    value={editForm.fullName || ''}
                    onChange={(e) => handleEditField('fullName', e.target.value)}
                    data-testid="edit-scholarship-fullName"
                  />
                </div>
              )}

              {/* Personal Information */}
              <h3 className="font-semibold text-lg mb-3 border-b pb-2">Personal Information</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {renderField("Father's Name", 'fatherName')}
                {renderField("Mother's Name", 'motherName')}
                {renderField('Date of Birth', 'dateOfBirth', { type: 'date', render: (v) => formatDisplayDate(v) || '—' })}
                {renderField('Gender', 'gender')}
                {renderField('Email', 'email', { type: 'email' })}
                {renderField('Phone', 'phone')}
                {renderField('WhatsApp', 'whatsapp')}
                {editMode ? (
                  <>
                    {renderField('Address', 'address', { colSpan: 3 })}
                    {renderField('City', 'city')}
                    {renderField('State', 'state')}
                    {renderField('Pincode', 'pincode')}
                  </>
                ) : (
                  <div className="col-span-3">
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">{selectedApp.address}, {selectedApp.city}, {selectedApp.state} - {selectedApp.pincode}</p>
                  </div>
                )}
              </div>

              {/* Family Information */}
              <h3 className="font-semibold text-lg mb-3 border-b pb-2">Family Information</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {renderField('Family Income', 'familyIncome', {
                  type: 'number',
                  render: (v) => `₹${parseInt(v || 0).toLocaleString()}`,
                })}
                {renderField('Family Members', 'familyMembers', { type: 'number' })}
                {renderField("Father's Occupation", 'fatherOccupation')}
              </div>

              {/* Academic Information */}
              <h3 className="font-semibold text-lg mb-3 border-b pb-2">Academic Information</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {renderField('Course', 'currentCourse')}
                {renderField('Duration (years)', 'courseDuration', { type: 'number' })}
                {renderField('Current Year', 'currentYear')}
                {renderField('Previous Marks (%)', 'previousMarks', {
                  type: 'number',
                  render: (v) => <span className="text-blue-600">{v}%</span>,
                })}
                {renderField('Admission Year', 'admissionYear')}
                {renderField('Institute', 'instituteName')}
                {editMode ? (
                  <>
                    {renderField('Institute Address', 'instituteAddress', { colSpan: 3 })}
                    {renderField('Institute City', 'instituteCity')}
                    {renderField('Institute State', 'instituteState')}
                  </>
                ) : (
                  <div className="col-span-3">
                    <p className="text-sm text-muted-foreground">Institute Address</p>
                    <p className="font-medium">{selectedApp.instituteAddress}, {selectedApp.instituteCity}, {selectedApp.instituteState}</p>
                  </div>
                )}
              </div>

              {/* Bank Details */}
              <h3 className="font-semibold text-lg mb-3 border-b pb-2">Bank Details (Institution)</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {renderField('Bank Name', 'instituteBankName')}
                {renderField('Account Number', 'instituteBankAccount')}
                {renderField('IFSC Code', 'instituteBankIfsc')}
                {renderField('Tuition Fee Amount', 'tuitionFeeAmount', {
                  type: 'number',
                  render: (v) => <span className="text-lg text-green-600">₹{parseInt(v || 0).toLocaleString()}</span>,
                })}
              </div>

              {/* Documents */}
              <h3 className="font-semibold text-lg mb-3 border-b pb-2">Uploaded Documents</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                {['photoIdProof', 'passportPhoto', 'marksheet', 'incomeProof', 'admissionLetter', 'courseProsectus'].map(doc => (
                  <div key={doc}>
                    <p className="text-sm text-muted-foreground capitalize">{doc.replace(/([A-Z])/g, ' $1')}</p>
                    {selectedApp[doc] ? (
                      <div className="flex flex-col gap-1">
                        <a 
                          href={resolveFileUrl(selectedApp[doc])}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1"
                          data-testid={`view-${doc}`}
                        >
                          <FileText className="w-4 h-4" /> View Document
                        </a>
                        <a 
                          href={`${resolveFileUrl(selectedApp[doc])}${resolveFileUrl(selectedApp[doc]).includes('?') ? '&' : '?'}download=1`}
                          className="text-green-600 hover:underline flex items-center gap-1 text-sm"
                          data-testid={`download-${doc}`}
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Not uploaded</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Additional Documents — admin-uploaded after initial submission */}
              <AdditionalDocumentsSection
                docs={selectedApp.additionalDocuments || []}
                onUpload={uploadAdditionalDoc}
                onDelete={deleteAdditionalDoc}
              />

              {editMode ? (
                <div className="mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Remarks</p>
                  <textarea
                    value={editForm.remarks || ''}
                    onChange={(e) => handleEditField('remarks', e.target.value)}
                    className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm"
                    placeholder="Optional remarks"
                    data-testid="edit-scholarship-remarks"
                  />
                </div>
              ) : (
                selectedApp.remarks && (
                  <div className="mb-6 p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Remarks</p>
                    <p className="font-medium">{selectedApp.remarks}</p>
                  </div>
                )
              )}

              <div className="flex justify-end gap-2">
                {selectedApp.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      className="text-red-600"
                      onClick={() => {
                        const remarks = prompt('Enter rejection reason (optional):');
                        updateApplicationStatus(selectedApp.id, 'rejected', remarks || '');
                      }}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => updateApplicationStatus(selectedApp.id, 'approved')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                  </>
                )}
                {selectedApp.status === 'approved' && (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowDisburseModal(true)}
                    data-testid="mark-disbursed-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Mark as Disbursed
                  </Button>
                )}
                <Button variant="outline" onClick={() => setSelectedApp(null)}>
                  Close
                </Button>
              </div>
            </div>
          </Card>

          {showDisburseModal && (
            <DisburseModal
              onClose={() => setShowDisburseModal(false)}
              onConfirm={async (file, remark) => {
                await submitDisbursement(file, remark);
                setShowDisburseModal(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ScholarshipApplications;

// ————————————————————————————————————————————————————————
// Disbursement modal — enforces proof file + remark before status change
// ————————————————————————————————————————————————————————
const DisburseModal = ({ onClose, onConfirm }) => {
  const [file, setFile] = React.useState(null);
  const [remark, setRemark] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!remark.trim()) {
      toast.error('Please add a disbursement remark (reference, date, bank, mode).');
      return;
    }
    if (!file) {
      toast.error('Please attach a scan of the cheque / DD / bank slip.');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(file, remark.trim());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record disbursement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="disburse-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              Mark as Disbursed
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Attach a scan of the cheque / DD / bank slip and add a disbursement remark
            with the payment reference, date, bank / branch and mode of payment.
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Disbursement Remark <span className="text-red-500">*</span></label>
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={4}
                placeholder="e.g. Cheque #123456, dated 12-Aug-2026, HDFC Bank Jodhpur branch, mode: Cheque, amount ₹50,000"
                className="mt-1 w-full text-sm bg-white border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                data-testid="disburse-remark-input"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700">Proof of Disbursement <span className="text-red-500">*</span></label>
              <p className="text-xs text-gray-500 mb-1">Cheque / DD image or PDF. Max ~10 MB.</p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-sm bg-white border border-input rounded-md px-3 py-2 w-full"
                data-testid="disburse-file-input"
              />
              {file && (
                <p className="text-xs text-gray-600 mt-1 truncate">Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="disburse-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="disburse-confirm"
            >
              {submitting ? 'Recording…' : 'Record Disbursement'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ————————————————————————————————————————————————————————
// Additional Documents subcomponent — admin can attach files later
// (e.g. documents received from the applicant over email post-submission).
// ————————————————————————————————————————————————————————
const AdditionalDocumentsSection = ({ docs, onUpload, onDelete }) => {
  const [file, setFile] = React.useState(null);
  const [label, setLabel] = React.useState('');
  const [note, setNote] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const submit = async () => {
    if (!file) {
      toast.error('Please choose a file to upload');
      return;
    }
    setUploading(true);
    try {
      await onUpload(file, label.trim(), note.trim());
      setFile(null);
      setLabel('');
      setNote('');
      if (inputRef.current) inputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const fmtSize = (n) => {
    if (!n && n !== 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="mb-6" data-testid="scholarship-additional-docs">
      <div className="flex items-center justify-between mb-3 border-b pb-2">
        <h3 className="font-semibold text-lg">Additional Documents</h3>
        <span className="text-xs text-gray-500">{docs.length} attached</span>
      </div>

      {/* Existing docs list */}
      {docs.length === 0 ? (
        <p className="text-sm text-gray-500 italic mb-3">
          No additional documents uploaded yet. Attach any supplementary files (marksheets, receipts, proofs) received from the applicant here.
        </p>
      ) : (
        <div className="space-y-2 mb-4">
          {docs.map((d) => {
            const uploadedAt = d.uploadedAt ? new Date(d.uploadedAt) : null;
            return (
              <div
                key={d.id}
                className="flex items-start justify-between gap-3 bg-white border border-gray-200 rounded-lg p-3"
                data-testid={`additional-doc-${d.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="font-medium text-sm truncate" title={d.label}>{d.label}</span>
                    <span className="text-xs text-gray-400">{fmtSize(d.sizeBytes)}</span>
                  </div>
                  {d.originalName && d.originalName !== d.label && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate" title={d.originalName}>Original: {d.originalName}</p>
                  )}
                  {d.note && (
                    <p className="text-xs text-gray-600 mt-1 italic whitespace-pre-wrap">{d.note}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Uploaded by <span className="font-medium">{d.uploadedBy}</span>
                    {uploadedAt && !isNaN(uploadedAt.getTime()) && (
                      <> · {formatDisplayDateTime(uploadedAt)}</>
                    )}
                  </p>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <a
                    href={resolveFileUrl(d.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                    data-testid={`view-additional-${d.id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </a>
                  <a
                    href={`${resolveFileUrl(d.url)}${resolveFileUrl(d.url).includes('?') ? '&' : '?'}download=1`}
                    className="text-green-600 hover:underline text-xs flex items-center gap-1"
                    data-testid={`download-additional-${d.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                  <button
                    type="button"
                    onClick={() => onDelete(d.id)}
                    className="text-red-600 hover:underline text-xs flex items-center gap-1"
                    data-testid={`delete-additional-${d.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Uploader */}
      <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload a new supporting document
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Updated Marksheet)"
            className="bg-white"
            data-testid="additional-doc-label"
          />
          <input
            ref={inputRef}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm bg-white border border-input rounded-md px-3 py-2"
            data-testid="additional-doc-file"
          />
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (e.g. received from applicant via email on 12 Aug)"
          rows={2}
          className="w-full text-sm bg-white border border-gray-200 rounded-md px-3 py-2 mt-3 focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="additional-doc-note"
        />
        <div className="flex justify-end mt-3">
          <Button
            onClick={submit}
            disabled={!file || uploading}
            data-testid="additional-doc-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            {uploading ? 'Uploading…' : 'Upload Document'}
          </Button>
        </div>
      </div>
    </div>
  );
};
